// 騎士の切り下がり (lanceCross) の光 = 「A 手前を回る浅い弧」(2026-10-02)。
//   経緯: 10-01 夜「腰の高さで横に流れる帯」(knight_spear_trail.ts) → 「ほぼ見えなくなった。矛先が光ってるのは見えるけどそうじゃない」。
//   原因 (Claude): この技の絵は槍を真横に構えた1枚 (leap_swing) だけで、真横から見た水平の払いは槍と同じ1本の線に潰れる。
//   → 見本 (そざい/fx/knight-cross-light-look-1002/ compare_light.png・票 knight-cross-light-look-1002) の4案から
//   ユーザー選択「A 手前を回る浅い弧」: 払いを少し上から見た形 = 横に長い楕円の手前側 (下側の弧)。
//   体の後ろ (奥・少し高い) から騎士の腰の前を下へたわんで通り、穂先で終わる三日月の面。
//   見本から読んだ形: 外側 (下) の縁がいちばん明るく、内側 (槍の線) へ向かって透けて溶ける。面は前寄りで最も太く、
//   穂先で尖り、尾は細い筋にほどける。中に速く動いた跡の細い筋目。冷たい白銀。
//   ゲームでの決まり (ユーザー 10-01): 光は穂先より前へ出ない (見本の絵は穂先の先まで伸びていたが、それは採らない)・
//   騎士と一緒に動く (払った場所に置き去りにしない)。
//   材料は図形1枚にしない (production.md「FXの作り方」): 面は筋の束 (太さ・長さ・濃さが1本ずつ違う) で描き、
//   外縁だけ明るい線＋にじみ。乱数は固定の種 = 毎フレーム同じ筋 (ちらつかない)。
// 位置の計算 (knightCrossArcShape) はテストできる。描画はブラウザだけ。

/** 体の後ろの端 (尾の根元) = 跳び始めの位置から後ろへ。見本の尾は背中の後ろ、槍の長さの半分ほど。 */
export const KNIGHT_ARC_BACK_PX = 95
/** 弧のいちばん低い所の深さ (槍の高さから下へ)。見本は胴の高さの半分ほど下 = 騎士の太ももの前。 */
export const KNIGHT_ARC_DIP_PX = 34
/** 尾の持ち上がり (槍の高さから上へ)。払いが奥 (騎士の体の後ろ) から回ってくる分。 */
export const KNIGHT_ARC_TAIL_RISE_PX = 12
/** 面の最大の太さ (外縁から内側へ)。見本は兜の高さほど。 */
export const KNIGHT_ARC_THICK_PX = 26
export const KNIGHT_ARC_SWEEP_MS = 70
export const KNIGHT_ARC_FADE_MS = 220

/** 楕円の手前側を、尾 (u=0) から穂先 (u=1) までたどる。front = 穂先 (前へ +)、x は前向きの局所座標、y は下が +。 */
export function knightArcPoint(u: number, front: number): { x: number; y: number; thick: number } {
  const back = -KNIGHT_ARC_BACK_PX
  const xc = (front + back) / 2
  const a = (front - back) / 2
  // 尾の側は奥へ少し回り込む (t > π = 楕円の上側) ので、尾だけ槍より高い。
  //   10-02 実画面: 回り込み 0.42rad・持ち上がり 26px では尾が騎士の後ろで兜より上へ鉤のように巻き上がり、縦の振りに見えた → 0.16rad・12px。
  const t = (1 - u) * (Math.PI + 0.16)
  const x = xc + a * Math.cos(t)
  let y = KNIGHT_ARC_DIP_PX * Math.sin(t)
  // 楕円を少し傾ける (前が下・後ろが上) = 尾が槍より上まで上がる。穂先 (u=1) は槍の高さちょうど。
  y -= KNIGHT_ARC_TAIL_RISE_PX * Math.max(0, (xc - x) / a) * 0.6
  // 太さ: 穂先で 0 (尖る)・前寄り (u≈0.72) で最大・尾で細い。
  const thick = KNIGHT_ARC_THICK_PX * Math.pow(Math.sin(Math.PI * Math.min(1, u / 0.72) * 0.5), 1.3)
    * (u > 0.72 ? Math.pow(Math.max(0, (1 - u) / 0.28), 0.7) : 1)
  return { x, y, thick }
}

/**
 * 時刻ごとの見える区間。ageMs = active に入ってからの ms。
 * head = 光の先 (尾から穂先へ走る・0..1)、tail = 消える時に尾から食われる位置 (0..1)、alpha = 全体の濃さ。払いの前・消えた後は null。
 */
export function knightCrossArcShape(ageMs: number): { head: number; tail: number; alpha: number; shrink: number } | null {
  if (ageMs < 0 || ageMs > KNIGHT_ARC_SWEEP_MS + KNIGHT_ARC_FADE_MS) return null
  const s = Math.min(1, ageMs / KNIGHT_ARC_SWEEP_MS)
  const head = 1 - (1 - s) * (1 - s)   // 出だしが速く、穂先で減速して止まる
  const q = Math.max(0, (ageMs - KNIGHT_ARC_SWEEP_MS) / KNIGHT_ARC_FADE_MS)
  return { head, tail: Math.min(head, 0.85 * q * q + 0.25 * q), alpha: (1 - q) * (1 - q * 0.5), shrink: 1 - 0.45 * q }
}

/**
 * 当たりの前端 (前向きの局所 px) = 光の先がいま通っている所 (10-02 ユーザー「あたってないように見えるのにあたっている判定になるのが理不尽」)。
 *   光が尾 (体の後ろ) から穂先へ走る 70ms の間、当たりはまだ光が来ていない前方には無い。走り切った後は穂先 (reach) まで。
 *   hits.ts meleeStrikeNow が実判定と dev H 表示の両方で使う。
 */
export function knightCrossStrikeFrontPx(ageMs: number, reach: number): number {
  if (ageMs >= KNIGHT_ARC_SWEEP_MS) return reach
  const s = Math.min(1, Math.max(0, ageMs) / KNIGHT_ARC_SWEEP_MS)
  return knightArcPoint(1 - (1 - s) * (1 - s), reach).x
}

const hash01 = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }
const STRANDS = Array.from({ length: 22 }, (_, i) => ({
  f: Math.pow(hash01(i * 3.1), 0.8),                 // 外縁 (0) → 内側 (1)
  u0: hash01(i * 7.7 + 1) * 0.35,                    // 筋の始まり (尾寄りのどこか)
  u1: 0.78 + hash01(i * 5.3 + 2) * 0.22,             // 筋の終わり (穂先寄り)
  w: 0.6 + hash01(i * 2.9 + 3) * 1.6,
  a: 0.35 + hash01(i * 4.1 + 4) * 0.65,
}))
const EDGE = 'rgba(236,244,255,1)'
const SILVER = 'rgba(200,218,246,1)'

/**
 * 描く。bodyX = 今の騎士の位置 (跳び下がりで動く)、y = 槍の高さ、dir = 前の向き、reach = 穂先までの距離 (当たりの前端と同じ)。
 */
export function drawKnightCrossArc(ctx: CanvasRenderingContext2D, bodyX: number, y: number, dir: number, reach: number, ageMs: number): void {
  const sh = knightCrossArcShape(ageMs)
  if (!sh || sh.alpha <= 0.003 || sh.head - sh.tail <= 0.01) return
  const N = 40
  const wx = (lx: number) => bodyX + dir * lx
  const outer: { x: number; y: number }[] = []
  const inner: { x: number; y: number }[] = []
  const us: number[] = []
  for (let i = 0; i <= N; i++) {
    const u = sh.tail + (sh.head - sh.tail) * (i / N)
    const p = knightArcPoint(u, reach)
    // 面は外縁 (下) から内側 (上 = 槍の線の方) へ。光っている所が前へ走る間は先端ほど細い (まだ振り切っていない)。
    const th = p.thick * sh.shrink * (0.55 + 0.45 * Math.min(1, sh.head / 0.95))
    us.push(u)
    outer.push({ x: wx(p.x), y: y + p.y })
    inner.push({ x: wx(p.x), y: y + p.y - th })
  }
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.lineCap = 'round'
  // ① 面: 外縁から内側へ透けるグラデーション。縦の帯ごとに分けて、太さに合わせる。
  for (let i = 0; i < N; i++) {
    const o0 = outer[i]!, o1 = outer[i + 1]!, n0 = inner[i]!, n1 = inner[i + 1]!
    const mid = (us[i]! + us[i + 1]!) / 2
    // 奥 (尾・体の後ろ) は暗く、前ほど明るい。
    const depth = 0.3 + 0.7 * Math.min(1, mid / 0.6)
    const g = ctx.createLinearGradient(0, (o0.y + o1.y) / 2, 0, (n0.y + n1.y) / 2)
    g.addColorStop(0, `rgba(210,226,250,${0.34 * depth * sh.alpha})`)
    g.addColorStop(0.45, `rgba(190,210,245,${0.12 * depth * sh.alpha})`)
    g.addColorStop(1, 'rgba(180,200,240,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(o0.x, o0.y); ctx.lineTo(o1.x, o1.y); ctx.lineTo(n1.x, n1.y); ctx.lineTo(n0.x, n0.y)
    ctx.closePath()
    ctx.fill()
  }
  // ② 筋目: 面の中を走る細い筋。1本ずつ太さ・長さ・濃さが違う。尾の側から消えていく区間は描かない。
  ctx.strokeStyle = SILVER
  for (const st of STRANDS) {
    const a0 = Math.max(st.u0, sh.tail), a1 = Math.min(st.u1, sh.head)
    if (a1 - a0 <= 0.02) continue
    ctx.lineWidth = st.w
    ctx.beginPath()
    const M = 16
    for (let k = 0; k <= M; k++) {
      const u = a0 + (a1 - a0) * (k / M)
      const p = knightArcPoint(u, reach)
      const th = p.thick * sh.shrink
      const px = wx(p.x), py = y + p.y - th * st.f * 0.92
      if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py)
    }
    ctx.globalAlpha = st.a * (1 - st.f * 0.7) * 0.42 * sh.alpha
    ctx.stroke()
  }
  // ③ 外縁: いちばん明るい線。前ほど太く明るく、にじみ付き。区間ごとに太さを変える。
  // 継ぎ目を丸めると加算で玉の連なりに割れる (render.ts drawContactSmearArc の 08-31 の注) = butt。
  ctx.lineCap = 'butt'
  ctx.strokeStyle = EDGE
  ctx.shadowColor = 'rgba(190,215,255,1)'
  for (let i = 0; i < N; i++) {
    const mid = (us[i]! + us[i + 1]!) / 2
    const lead = Math.min(1, mid / 0.8)
    const tipTaper = mid > 0.94 ? Math.max(0.25, (1 - mid) / 0.06) : 1
    ctx.globalAlpha = sh.alpha * (0.18 + 0.72 * lead) * tipTaper
    ctx.lineWidth = (0.8 + 2.6 * lead) * tipTaper * sh.shrink
    ctx.shadowBlur = 9 * lead * sh.alpha
    ctx.beginPath()
    ctx.moveTo(outer[i]!.x, outer[i]!.y)
    ctx.lineTo(outer[i + 1]!.x, outer[i + 1]!.y)
    ctx.stroke()
  }
  ctx.restore()
  ctx.globalAlpha = 1
}
